import { useQuery, useQueryClient } from '@tanstack/react-query';
import { comptoirAPI, comptoirCaisseAPI, comptoirDashboardAPI, comptoirProductionAPI, comptoirServicesAPI, comptoirPaiementsAPI, projetsAPI, servicesAPI } from '../../services/api';
import {
  ComptoirClient, ComptoirClientOption, ComptoirClientSituation, ComptoirDesignation, ComptoirOffre, ComptoirOptions, ComptoirStats, ComptoirUserOption, Paginated,
  ComptoirService, ComptoirServiceDetail, ComptoirServiceStats, ComptoirHistoryLine, ComptoirServiceProduction, ComptoirProductionTravaux,
  ComptoirCaisseJournal, ComptoirCaisseMeta, ComptoirCaisseParams, ComptoirSuivi, ComptoirSuiviService, ComptoirSuiviMeta, ComptoirEtape,
  ComptoirDashboard, ComptoirFinance,
} from '../../types/comptoir';

/**
 * Query hooks for the Comptoir module.
 *
 * Every key starts with 'comptoir' — the /api/comptoir/... slug the
 * realtime-invalidation middleware broadcasts — so any Comptoir mutation by
 * another user refreshes these screens. The fixed dropdown lists never change
 * at runtime, so they live under their own root and are cached for good.
 */
export interface ComptoirListParams {
  skip: number;
  limit: number;
  search?: string;
}

export const comptoirKeys = {
  root: ['comptoir'] as const,
  offres: (params: ComptoirListParams) => ['comptoir', 'offres', params] as const,
  offresDeleted: ['comptoir', 'offres', 'deleted'] as const,
  clients: (params: ComptoirListParams) => ['comptoir', 'clients', params] as const,
  clientsDeleted: ['comptoir', 'clients', 'deleted'] as const,
  // A client created from the offre wizard must show up in the picker at once
  clientOptions: ['comptoir', 'client-options'] as const,
  stats: ['comptoir', 'stats'] as const,
  // Learnt from the travaux: refreshed by any offre mutation
  designations: ['comptoir', 'designations'] as const,
  users: ['comptoir-users'] as const,
  options: ['comptoir-options'] as const,
};

export function useComptoirOffres(params: ComptoirListParams, enabled = true) {
  return useQuery<Paginated<ComptoirOffre>>({
    queryKey: comptoirKeys.offres(params),
    queryFn: () => comptoirAPI.getOffres(params),
    enabled,
    keepPreviousData: true,
  });
}

export function useComptoirDeletedOffres(enabled: boolean) {
  return useQuery<ComptoirOffre[]>({
    queryKey: comptoirKeys.offresDeleted,
    queryFn: () => comptoirAPI.getDeletedOffres(),
    enabled,
  });
}

export function useComptoirClients(params: ComptoirListParams, enabled = true) {
  return useQuery<Paginated<ComptoirClient>>({
    queryKey: comptoirKeys.clients(params),
    queryFn: () => comptoirAPI.getClients(params),
    enabled,
    keepPreviousData: true,
  });
}

/** Situation client (client page + relevé); under the comptoir root so any payment,
 *  delivery or offre change refreshes it. */
export function useComptoirClientSituation(id: number | null) {
  return useQuery<ComptoirClientSituation>({
    queryKey: ['comptoir', 'client', id || 0, 'situation'],
    queryFn: () => comptoirAPI.getClientSituation(id as number),
    enabled: !!id,
  });
}

export function useComptoirDeletedClients(enabled: boolean) {
  return useQuery<ComptoirClient[]>({
    queryKey: comptoirKeys.clientsDeleted,
    queryFn: () => comptoirAPI.getDeletedClients(),
    enabled,
  });
}

export function useComptoirClientOptions(enabled = true) {
  return useQuery<ComptoirClientOption[]>({
    queryKey: comptoirKeys.clientOptions,
    queryFn: () => comptoirAPI.getClientOptions(),
    enabled,
  });
}

export function useComptoirDesignations(enabled = true) {
  return useQuery<ComptoirDesignation[]>({
    queryKey: comptoirKeys.designations,
    queryFn: () => comptoirAPI.getDesignations(),
    enabled,
  });
}

export function useComptoirUsersOptions(enabled = true) {
  return useQuery<ComptoirUserOption[]>({
    queryKey: comptoirKeys.users,
    queryFn: () => comptoirAPI.getUsersOptions(),
    enabled,
  });
}

export function useComptoirStats() {
  return useQuery<ComptoirStats>({
    queryKey: comptoirKeys.stats,
    queryFn: () => comptoirAPI.getStats(),
  });
}

export function useComptoirOptions() {
  return useQuery<ComptoirOptions>({
    queryKey: comptoirKeys.options,
    queryFn: () => comptoirAPI.getOptions(),
    staleTime: Infinity,
  });
}

/* ---------------- Services & Projets ---------------- */
export interface ComptoirServicesParams extends ComptoirListParams {
  etat?: string;
  paiement?: string;
  en_retard?: boolean;
}

export const comptoirServiceKeys = {
  list: (params: ComptoirServicesParams) => ['comptoir', 'services', params] as const,
  detail: (id: number) => ['comptoir', 'service', id] as const,
  history: (id: number) => ['comptoir', 'service', id, 'history'] as const,
  stats: ['comptoir', 'services-stats'] as const,
  dus: (clientId: number) => ['comptoir', 'dus', clientId] as const,
};

export function useComptoirServices(params: ComptoirServicesParams) {
  return useQuery<Paginated<ComptoirService>>({
    queryKey: comptoirServiceKeys.list(params),
    queryFn: () => comptoirServicesAPI.list(params),
    keepPreviousData: true,
  });
}

export function useComptoirServicesStats() {
  return useQuery<ComptoirServiceStats>({
    queryKey: comptoirServiceKeys.stats,
    queryFn: () => comptoirServicesAPI.stats(),
  });
}

export function useComptoirService(id: number | null) {
  return useQuery<ComptoirServiceDetail>({
    queryKey: comptoirServiceKeys.detail(id || 0),
    queryFn: () => comptoirServicesAPI.get(id as number),
    enabled: !!id,
    // its bureau d'études state changes from Production (that broadcasts 'production', not
    // 'comptoir'): reload whenever the page opens
    staleTime: 0,
  });
}

export function useComptoirServiceHistory(id: number | null) {
  return useQuery<ComptoirHistoryLine[]>({
    queryKey: comptoirServiceKeys.history(id || 0),
    queryFn: () => comptoirServicesAPI.historique(id as number),
    enabled: !!id,
  });
}

/** Production state of a service (planning + suivi). Keyed under ['suivi'] so the realtime
 *  invalidation fires when the atelier checks a step; refetched on every visit because
 *  planning changes broadcast another slug ('planning'). */
export function useComptoirServiceProduction(id: number | null) {
  return useQuery<ComptoirServiceProduction>({
    queryKey: ['suivi', 'comptoir-service', id || 0],
    queryFn: () => comptoirServicesAPI.production(id as number),
    enabled: !!id,
    staleTime: 0,
  });
}

/** The travaux behind a production row (Gestion Projets / Services, Suivi). Read through
 *  /api/projets|services but keyed under 'comptoir': only Comptoir mutations (offre edit,
 *  livraison) change them, and those broadcast the 'comptoir' slug. */
export function useComptoirProductionTravaux(type: 'projet' | 'service', id: number | null, enabled = true) {
  return useQuery<ComptoirProductionTravaux>({
    queryKey: ['comptoir', 'production-travaux', type, id || 0],
    queryFn: () => (type === 'projet' ? projetsAPI : servicesAPI).getComptoirTravaux(id as number),
    enabled: enabled && !!id,
  });
}

export function useComptoirDus(clientId: number | null, enabled = true) {
  return useQuery<ComptoirService[]>({
    queryKey: comptoirServiceKeys.dus(clientId || 0),
    queryFn: () => comptoirPaiementsAPI.dus(clientId as number),
    enabled: enabled && !!clientId,
  });
}

export function useInvalidateComptoir() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: comptoirKeys.root });
}

/** Caisse journal: under the comptoir root, so a cash payment (Encaisser) refreshes it too. */
export function useComptoirCaisse(params: ComptoirCaisseParams) {
  return useQuery<ComptoirCaisseJournal>({
    queryKey: ['comptoir', 'caisse', params],
    queryFn: () => comptoirCaisseAPI.journal(params),
    keepPreviousData: true,
  });
}

export function useComptoirCaisseMeta(enabled = true) {
  return useQuery<ComptoirCaisseMeta>({
    queryKey: ['comptoir', 'caisse-meta'],
    queryFn: () => comptoirCaisseAPI.meta(),
    enabled,
  });
}

/* Suivi de production: under the comptoir root, so any étape / saisie / service change refreshes it. */
export function useComptoirSuivi() {
  return useQuery<ComptoirSuivi>({
    queryKey: ['comptoir', 'production'],
    queryFn: () => comptoirProductionAPI.suivi(),
    keepPreviousData: true,
    // it shows the bureau d'études state (plan non validé, travaux modifiés « Vu »), changed by
    // /api/production/... routes that broadcast 'production', not 'comptoir': reload on open
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useComptoirSuiviService(serviceId: number | null, enabled = true) {
  return useQuery<ComptoirSuiviService>({
    queryKey: ['comptoir', 'production', 'service', serviceId],
    queryFn: () => comptoirProductionAPI.service(serviceId as number),
    enabled: enabled && !!serviceId,
  });
}

export function useComptoirSuiviMeta(enabled = true) {
  return useQuery<ComptoirSuiviMeta>({
    queryKey: ['comptoir', 'production-meta'],
    queryFn: () => comptoirProductionAPI.meta(),
    enabled,
  });
}

/** One étape with its saisies history (the Saisir dialog). */
export function useComptoirEtape(id: number | null) {
  return useQuery<ComptoirEtape>({
    queryKey: ['comptoir', 'production', 'etape', id],
    queryFn: () => comptoirProductionAPI.etape(id as number),
    enabled: !!id,
    staleTime: 0,
  });
}

/** Tableau de bord: under the comptoir root, so any Comptoir mutation refreshes it. */
export function useComptoirDashboard() {
  return useQuery<ComptoirDashboard>({
    queryKey: ['comptoir', 'dashboard'],
    queryFn: () => comptoirDashboardAPI.get(),
  });
}

/** Suivi financier: under the comptoir root, so a payment anywhere refreshes it. */
export function useComptoirFinance() {
  return useQuery<ComptoirFinance>({
    queryKey: ['comptoir', 'finance'],
    queryFn: () => comptoirDashboardAPI.finance(),
  });
}
